<!-- src/components/GlobalBackgroundWrapper.vue -->
<template>
  <div class="global-background-wrapper">
    <!-- 背景层 -->
    <div
        v-if="showBackgroundLayer"
        class="background-layer"
        :style="backgroundLayerStyle"
        @click="handleBackgroundClick"
    ></div>

    <!-- 内容容器 -->
    <div class="content-container" :style="contentContainerStyle">
      <slot></slot>
    </div>

    <!-- 背景设置按钮 -->
    <div
        v-if="showSettingsButton"
        :class="['background-settings-btn', buttonPositionClass]"
        @mouseenter="buttonHover = true"
        @mouseleave="buttonHover = false"
        @click="toggleSettingsDrawer"
    >
      <transition name="fade-fast" mode="out-in">
        <i
            v-if="buttonHover || isSettingsOpen"
            key="hover"
            class="el-icon-setting"
        ></i>
        <i v-else key="normal" class="el-icon-picture-outline"></i>
      </transition>
      <transition name="slide-fade">
        <span v-if="buttonHover || isSettingsOpen" class="btn-text">
          背景设置
        </span>
      </transition>
      <div v-if="buttonHover" class="btn-tooltip">点击调整页面背景</div>
    </div>

    <!-- 背景设置抽屉 -->
    <el-drawer
        :title="drawerTitle"
        :visible.sync="drawerVisible"
        direction="rtl"
        :size="drawerSize"
        :wrapper-closable="false"
        :before-close="handleDrawerClose"
        custom-class="background-settings-drawer"
    >
      <BackgroundSettingsPanel
          v-model="currentSettings"
          :page-key="pageKey"
          @apply="applySettings"
          @reset="resetSettings"
          @close="closeDrawer"
          @change="handleSettingsChange"
          @preset-selected="handlePresetSelected"
          @custom-image-uploaded="handleCustomImageUploaded"
          @custom-image-removed="handleCustomImageRemoved"
          @quick-blur-set="handleQuickBlurSet"
      />
    </el-drawer>
  </div>
</template>

<script>
import { getPageBackgroundSettings, savePageBackgroundSettings, migrateOldSettings } from '@/utils/storage'
import BackgroundSettingsPanel from './BackgroundSettingsPanel.vue'

export default {
  name: 'GlobalBackgroundWrapper',
  components: { BackgroundSettingsPanel },
  props: {
    pageKey: {
      type: String,
      default: 'global'
    },
    defaultBackground: {
      type: Object,
      default: null
    },
    showSettings: {
      type: Boolean,
      default: true
    },
    settingsPosition: {
      type: String,
      default: 'top-right',
      validator: (value) => ['top-right', 'top-left', 'bottom-right', 'bottom-left'].includes(value)
    },
    autoApply: {
      type: Boolean,
      default: false
    },
    enableRealtimePreview: {
      type: Boolean,
      default: true
    }
  },
  data() {
    migrateOldSettings()

    let settings = getPageBackgroundSettings(this.pageKey)
    if (!settings && this.defaultBackground) {
      settings = this.defaultBackground
    }

    return {
      currentSettings: settings || this.getDefaultSettings(),
      originalSettings: null,
      drawerVisible: false,
      isSettingsOpen: false,
      buttonHover: false,
      isMobile: false,
      isClosing: false,
      hasUnsavedChanges: false
    }
  },
  computed: {
    drawerSize() {
      return this.isMobile ? '85%' : '320px'
    },
    drawerTitle() {
      const titles = {
        'global': '全局背景设置',
        'user-management': '用户管理背景设置',
        'coach-management': '教练管理背景设置',
        'my-reservations': '我的预约背景设置',
        'reservation': '预约练车背景设置',
        'coach-reservations': '教练预约管理背景设置',
        'schedule': '排班管理背景设置'
      }
      return titles[this.pageKey] || '背景设置'
    },
    buttonPositionClass() {
      return `position-${this.settingsPosition}`
    },
    showSettingsButton() {
      return this.showSettings && !this.isMobile
    },
    showBackgroundLayer() {
      return this.currentSettings.type !== 'none'
    },
    backgroundLayerStyle() {
      if (this.currentSettings.type === 'none') return {}

      const style = {
        transition: 'all 0.3s ease'
      }

      if (this.currentSettings.type === 'image') {
        style.backgroundImage = `url(${this.currentSettings.imageUrl})`
        style.filter = `blur(${this.currentSettings.blurLevel}px)`
        style.opacity = this.currentSettings.opacity
      } else if (this.currentSettings.type === 'color') {
        style.backgroundColor = this.currentSettings.backgroundColor
      }

      return style
    },
    contentContainerStyle() {
      const style = {}

      if (this.currentSettings.type === 'color') {
        style.backgroundColor = this.currentSettings.backgroundColor
      } else if (this.currentSettings.type === 'none') {
        style.backgroundColor = 'transparent'
      }

      return style
    }
  },
  mounted() {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
    this.originalSettings = JSON.parse(JSON.stringify(this.currentSettings))
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile)
  },
  watch: {
    currentSettings: {
      handler(newSettings) {
        if (this.enableRealtimePreview) {
          this.$emit('settings-changed', newSettings)
          this.hasUnsavedChanges = JSON.stringify(newSettings) !== JSON.stringify(this.originalSettings)
        }
      },
      deep: true
    }
  },
  methods: {
    getDefaultSettings() {
      return {
        type: 'image',
        imageUrl: 'https://images4.alphacoders.com/254/thumb-1920-254084.jpg',
        blurLevel: 5,
        opacity: 0.8,
        backgroundColor: '#f0f2f5',
        applyGlobally: false,
        customImage: null
      }
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
      if (this.isMobile && this.drawerVisible) {
        this.drawerVisible = false
      }
    },

    toggleSettingsDrawer() {
      if (!this.drawerVisible) {
        this.originalSettings = JSON.parse(JSON.stringify(this.currentSettings))
        this.hasUnsavedChanges = false
      }
      this.drawerVisible = !this.drawerVisible
      this.isSettingsOpen = this.drawerVisible
    },

    handleDrawerClose(done) {
      if (this.isClosing) return
      this.isClosing = true

      if (this.hasUnsavedChanges) {
        this.showConfirmDialog(
            '您有未保存的设置，是否保存？',
            '提示',
            {
              confirmButtonText: '保存',
              cancelButtonText: '放弃',
              type: 'warning'
            }
        ).then(() => {
          // 保存设置
          this.saveSettings()
          this.originalSettings = JSON.parse(JSON.stringify(this.currentSettings))
          this.hasUnsavedChanges = false
          done()
        }).catch((action) => {
          if (action === 'cancel') {
            // 放弃更改，恢复原始设置
            this.currentSettings = JSON.parse(JSON.stringify(this.originalSettings))
            this.hasUnsavedChanges = false
            done()
          } else {
            // 点击关闭按钮，保持打开状态
            this.isClosing = false
          }
        })
      } else {
        done()
      }
    },

    // 安全的确认框方法
    showConfirmDialog(message, title, options = {}) {
      return new Promise((resolve, reject) => {
        this.$confirm(message, title, {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          ...options
        })
            .then(() => {
              resolve()
            })
            .catch((action) => {
              if (action === 'cancel' || action === 'close') {
                reject('cancel')
              } else {
                reject(action)
              }
            })
      })
    },

    closeDrawer() {
      if (this.hasUnsavedChanges) {
        this.showConfirmDialog(
            '您有未保存的设置，是否保存？',
            '提示',
            {
              confirmButtonText: '保存',
              cancelButtonText: '放弃',
              type: 'warning'
            }
        ).then(() => {
          this.applySettings()
        }).catch((action) => {
          if (action === 'cancel') {
            // 放弃更改，恢复原始设置
            this.currentSettings = JSON.parse(JSON.stringify(this.originalSettings))
            this.hasUnsavedChanges = false
            this.drawerVisible = false
            this.isSettingsOpen = false
          }
          // 其他情况保持打开
        })
      } else {
        this.drawerVisible = false
        this.isSettingsOpen = false
      }
    },

    applySettings() {
      this.saveSettings()
      this.originalSettings = JSON.parse(JSON.stringify(this.currentSettings))
      this.hasUnsavedChanges = false
      this.drawerVisible = false
      this.isSettingsOpen = false
      this.$message.success('背景设置已保存')
    },

    saveSettings() {
      const saveKey = this.currentSettings.applyGlobally ? 'global' : this.pageKey
      savePageBackgroundSettings(saveKey, this.currentSettings)

      if (this.currentSettings.applyGlobally && this.pageKey !== 'global') {
        savePageBackgroundSettings(this.pageKey, this.currentSettings)
      }
    },

    resetSettings() {
      this.showConfirmDialog(
          '确定要恢复默认背景设置吗？',
          '提示',
          {
            type: 'warning',
            confirmButtonText: '恢复默认',
            cancelButtonText: '取消'
          }
      ).then(() => {
        this.currentSettings = this.getDefaultSettings()
        this.saveSettings()
        this.originalSettings = JSON.parse(JSON.stringify(this.currentSettings))
        this.hasUnsavedChanges = false
        this.$message.success('已恢复默认设置')
      }).catch(() => {
        // 用户取消操作，不做任何处理
      })
    },

    handleBackgroundClick() {
      if (this.drawerVisible) {
        this.drawerVisible = false
        this.isSettingsOpen = false
      }
    },

    handleSettingsChange(changes) {
      Object.assign(this.currentSettings, changes)
      this.$emit('settings-change', changes)
    },

    handlePresetSelected(preset) {
      this.$message.info(`已选择「${preset.name}」背景`)
    },

    handleCustomImageUploaded() {
      this.$message.success('自定义背景已设置')
    },

    handleCustomImageRemoved() {
      this.$message.info('已移除自定义背景')
    },

    handleQuickBlurSet(level) {
      this.$message.info(`模糊度已设置为 ${level}`)
    }
  }
}
</script>

<style scoped>
.global-background-wrapper {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
}

.background-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  background-repeat: no-repeat;
  z-index: 0;
}

.content-container {
  position: relative;
  z-index: 1;
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

.background-settings-btn {
  position: fixed;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #dcdfe6;
  border-radius: 20px;
  padding: 8px 12px 8px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  font-size: 14px;
  color: #606266;
}

.background-settings-btn:hover {
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.background-settings-btn:hover .btn-tooltip {
  opacity: 1;
  transform: translateY(0);
}

.position-top-right {
  top: 100px;
  right: 20px;
}

.position-top-left {
  top: 100px;
  left: 20px;
}

.position-bottom-right {
  bottom: 30px;
  right: 20px;
}

.position-bottom-left {
  bottom: 30px;
  left: 20px;
}

.background-settings-btn i {
  font-size: 16px;
  transition: color 0.3s ease;
}

.background-settings-btn:hover i {
  color: #409EFF;
}

.btn-text {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.btn-tooltip {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease;
  z-index: 1001;
}

.btn-tooltip::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 4px 4px 0;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.8) transparent transparent;
}

/* 移动端样式 */
@media (max-width: 768px) {
  .background-settings-btn {
    top: auto;
    bottom: 20px;
    right: 20px;
    padding: 10px;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    justify-content: center;
  }

  .btn-text {
    display: none;
  }

  .background-layer {
    background-attachment: scroll;
  }
}

/* 动画效果 */
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.15s ease;
}

.fade-fast-enter,
.fade-fast-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.2s ease;
}

.slide-fade-leave-active {
  transition: all 0.15s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
</style>
